/* eslint-disable @typescript-eslint/no-explicit-any */
import { Ref, useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/shared/components/ui/alert';
import { Upload, X, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { PlanSelector } from '@/features/Subscriptions/components/PlanSelector';
import { PendingPaymentModal } from '@/features/Subscriptions/components/PendingPaymentModal';
import { ValidatorModal } from './ValidatorModal';
import { TrialSuccessModal } from './TrialSuccessModal';
import { createOrganizationSchema } from '../schemas/organizationSchema';
import { useOrganizationSubmit } from '../hooks/useOrganizationSubmit';
import { useImageUpload } from '../hooks/useImageUpload';
import { getLocalizedText } from '@/shared/utils/i18nHelper';
import { normalizeUrl } from '@/shared/utils/urlHelper';
import type { Plan } from '@/features/Subscriptions/types/subscription-types';
import { analyticsService } from '@/shared/services/analytics';
import { MagicFillButton } from '@/shared/components/form/MagicFillButton';
import { InputWithMagicFill } from '@/shared/components/form/InputWithMagicFill';
import { TextareaWithMagicFill } from '@/shared/components/form/TextareaWithMagicFill';
import { getOrganizationFormMetadata } from '@/shared/utils/magicFillMetadataHelpers';
import { MagicFillProvider } from '@/shared/contexts/MagicFillContext';

interface OrganizationStepProps {
  formRef: Ref<HTMLFormElement>;
  onSubmittingChange?: (isSubmitting: boolean) => void;
  onFormCompleteChange?: (isComplete: boolean) => void;
}

// Opções de tamanho da empresa
const COMPANY_SIZES = [
  { value: "1-10", labelKey: "auth.onboarding.organization.sizes.1-10" },
  { value: "11-50", labelKey: "auth.onboarding.organization.sizes.11-50" },
  { value: "51-200", labelKey: "auth.onboarding.organization.sizes.51-200" },
  { value: "201-500", labelKey: "auth.onboarding.organization.sizes.201-500" },
  { value: "501-1000", labelKey: "auth.onboarding.organization.sizes.501-1000" },
  { value: "1001-5000", labelKey: "auth.onboarding.organization.sizes.1001-5000" },
  { value: "5000+", labelKey: "auth.onboarding.organization.sizes.5000+" },
];

// Opções de estágio da empresa
const COMPANY_STAGES = [
  { value: "idea", labelKey: "auth.onboarding.organization.stages.idea" },
  { value: "startup", labelKey: "auth.onboarding.organization.stages.startup" },
  { value: "growth", labelKey: "auth.onboarding.organization.stages.growth" },
  { value: "established", labelKey: "auth.onboarding.organization.stages.established" },
  { value: "enterprise", labelKey: "auth.onboarding.organization.stages.enterprise" },
];

export const OrganizationStep = ({ formRef, onSubmittingChange, onFormCompleteChange }: OrganizationStepProps) => {
  const { t, i18n } = useTranslation();
  const [isMagicFillLoading, setIsMagicFillLoading] = useState(false);
  const [hasTriggeredAutoFill, setHasTriggeredAutoFill] = useState(false);
  const [showTestPopup, setShowTestPopup] = useState(false);
  const magicFillButtonRef = useRef<HTMLDivElement>(null);

  // Mostrar popup apenas na primeira vez que o usuário vê o Magic Fill
  useEffect(() => {
    const alreadyShown = typeof window !== 'undefined'
      && window.localStorage.getItem('magic_fill_intro_shown') === 'true';
    if (alreadyShown) return;

    const timer = setTimeout(() => {
      setShowTestPopup(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const dismissMagicFillIntro = () => {
    setShowTestPopup(false);
    try {
      window.localStorage.setItem('magic_fill_intro_shown', 'true');
    } catch {
      // storage indisponível — silencioso, não bloqueia fluxo
    }
  };
  
  const logo = useImageUpload({
    storageKey: 'onboarding_logo_preview',
    errorMessage: t('auth.onboarding.organization.logoSizeError')
  });
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [isPlanSelectorOpen, setIsPlanSelectorOpen] = useState(false);

  // Fechar popup quando usuário clicar em qualquer campo do formulário
  useEffect(() => {
    const handleFormFieldFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      // Verifica se o elemento focado é um input, textarea ou select
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        if (showTestPopup) {
          dismissMagicFillIntro();
        }
      }
    };

    // Adicionar listener no documento para capturar foco em qualquer campo
    document.addEventListener('focusin', handleFormFieldFocus);

    return () => {
      document.removeEventListener('focusin', handleFormFieldFocus);
    };
  }, [showTestPopup]);
  
  const {
    onSubmit,
    isSubmitting,
    error,
    planError,
    setPlanError,
    isWaitlistModalOpen,
    isPaymentModalOpen,
    pendingInvoice,
    isTrialSuccessModalOpen,
    trialEndDate,
    trialCredits,
    handleRequestAccess,
  } = useOrganizationSubmit(selectedPlan, logo.file);
  
  // Notificar mudanças no estado de submitting
  useEffect(() => {
    if (onSubmittingChange) {
      onSubmittingChange(isSubmitting);
    }
  }, [isSubmitting, onSubmittingChange]);

  // Restaurar logo preview do sessionStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedLogoPreview = sessionStorage.getItem('onboarding_logo_preview');
      if (savedLogoPreview && logo.inputRef.current) {
        fetch(savedLogoPreview)
          .then(res => res.blob())
          .then(blob => {
            const file = new File([blob], 'logo.png', { type: blob.type });
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(file);
            if (logo.inputRef.current) {
              logo.inputRef.current.files = dataTransfer.files;
              const event = new Event('change', { bubbles: true });
              logo.inputRef.current.dispatchEvent(event);
            }
          })
          .catch(err => console.error('Erro ao restaurar logo preview:', err));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const organizationSchema = createOrganizationSchema(t);
  
  // Load saved data from localStorage
  const savedOrgData = typeof window !== 'undefined' 
    ? JSON.parse(sessionStorage.getItem('onboarding_organization') || '{}') 
    : {};

  const organizationForm = useForm<z.infer<typeof organizationSchema>>({
    resolver: zodResolver(organizationSchema),
    defaultValues: {
      name: savedOrgData.name || "",
      info: savedOrgData.info || "",
      industry: savedOrgData.industry || "",
      size: savedOrgData.size || "",
      stage: savedOrgData.stage || "",
      website: savedOrgData.website || "",
    },
  });

  // Save form data to localStorage on change
  useEffect(() => {
    const subscription = organizationForm.watch((value) => {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('onboarding_organization', JSON.stringify(value));
      }
    });
    return () => subscription.unsubscribe();
  }, [organizationForm]);

  const handleSubmit = async (values: z.infer<typeof organizationSchema>) => {
    await onSubmit(values);
  };

  // Função centralizada para aplicar sugestões do Magic Fill
  const handleApplySuggestions = (suggested: Record<string, any>) => {
    console.log("📝 [OrganizationStep] ===== APLICANDO SUGESTÕES NO FORMULÁRIO =====");
    const currentValues = organizationForm.getValues();
    console.log("📝 [OrganizationStep] Valores atuais do formulário:", currentValues);
    console.log("📝 [OrganizationStep] Sugestões recebidas:", suggested);
    
    Object.entries(suggested).forEach(([key, value]) => {
      const fieldExists = Object.prototype.hasOwnProperty.call(currentValues, key);
      console.log(`📝 [OrganizationStep] Campo "${key}":`, {
        existe: fieldExists,
        valorSugerido: value,
        tipoValorSugerido: typeof value
      });
      
      if (fieldExists) {
        console.log(`  ✅ Aplicando setValue("${key}", ${JSON.stringify(value)})`);
        organizationForm.setValue(key as any, value as any, {
          shouldValidate: true,
          shouldDirty: true,
        });
      } else {
        console.log(`  ❌ Campo "${key}" não existe no formulário`);
      }
    });
    
    console.log("📝 [OrganizationStep] Valores do formulário após aplicação:", organizationForm.getValues());
    console.log("📝 [OrganizationStep] ===== FIM DA APLICAÇÃO =====");
  };

  // Auto-trigger Magic Fill quando sair de campos importantes (nome ou website)
  const handleAutoFillTrigger = () => {
    const nameValue = organizationForm.getValues('name');
    const websiteValue = organizationForm.getValues('website');
    
    // Só dispara se tiver nome OU website preenchido e ainda não foi disparado
    const hasValidContent = 
      (nameValue && nameValue.trim().length > 3) || 
      (websiteValue && websiteValue.trim().length > 5);
    
    if (hasValidContent && !hasTriggeredAutoFill && !isMagicFillLoading) {
      setHasTriggeredAutoFill(true);
      // Simula clique no botão Magic Fill
      magicFillButtonRef.current?.querySelector('button')?.click();
    }
  };

  // Verificar se todos os campos obrigatórios estão preenchidos
  const organizationFormValues = organizationForm.watch();
  const isOrganizationFormComplete = Boolean(
    organizationFormValues.name?.trim() &&
    organizationFormValues.info?.trim() &&
    organizationFormValues.industry?.trim() &&
    organizationFormValues.size?.trim() &&
    organizationFormValues.stage?.trim() &&
    organizationFormValues.website?.trim() &&
    selectedPlan !== null &&
    Object.keys(organizationForm.formState.errors).length === 0
  );

  // Notificar mudanças no estado de formulário completo
  useEffect(() => {
    if (onFormCompleteChange) {
      onFormCompleteChange(isOrganizationFormComplete);
    }
  }, [isOrganizationFormComplete, onFormCompleteChange]);

  return (
    <MagicFillProvider>
      <PlanSelector 
        isOpen={isPlanSelectorOpen}
        onClose={() => setIsPlanSelectorOpen(false)}
        onSelectPlan={(plan) => {
          setSelectedPlan(plan);
          setPlanError('');
        }}
        selectedPlanId={selectedPlan?.id}
      />
      
      <div className="w-full max-w-6xl space-y-6 animate-fade-in">
        <div className="flex justify-center">
          <img 
            src="/assets/logo.png" 
            alt="Prodgy Logo" 
            className="h-16 w-auto"
          />
        </div>
        
        {/* Title outside card */}
        <div className="text-center px-4 space-y-2">
          <h1 className="text-2xl font-medium tracking-tight">
            {t("auth.onboarding.organization.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("auth.onboarding.organization.description")}
          </p>
        </div>
        
        
        <div className="w-full max-w-5xl mx-auto">
          <Card className="shadow-lg">
          <CardContent className="px-8 py-8">
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{t("auth.onboarding.errors.title")}</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Form {...organizationForm}>
              <form ref={formRef} onSubmit={organizationForm.handleSubmit(handleSubmit)}>
                {/* Grid: Campos à esquerda, Planos à direita */}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-0 -m-8">
                  {/* Coluna Esquerda - Campos do Formulário */}
                  <div className="space-y-6 p-8 relative">
                    {/* Botões de ação com hint integrado */}
                    <div className="absolute top-2 right-2" ref={magicFillButtonRef}>
                      <MagicFillButton
                        formType="organization_onboarding"
                        getCurrentValues={() => organizationForm.getValues()}
                        metadata={{ 
                          onboarding: true,
                          fieldMetadata: getOrganizationFormMetadata()
                        }}
                        onLoadingChange={(isLoading) => {
                          setIsMagicFillLoading(isLoading);
                          // Fechar hint assim que clicar no Magic Button
                          if (isLoading) {
                            dismissMagicFillIntro();
                          }
                        }}
                        onApplySuggestions={(suggested) => {
                          handleApplySuggestions(suggested);
                        }}
                        size="icon"
                        className="h-8 w-8"
                        showClearButton={true}
                        onClear={() => {
                          organizationForm.reset({
                            name: "",
                            info: "",
                            industry: "",
                            size: "",
                            stage: "",
                            website: "",
                          });
                          logo.handleRemove();
                          // Resetar flag para permitir novo auto-trigger após limpar
                          setHasTriggeredAutoFill(false);
                          if (typeof window !== 'undefined') {
                            sessionStorage.removeItem('onboarding_organization');
                          }
                        }}
                        showHint={showTestPopup}
                        hintText={
                          <>
                            <span className="font-semibold block mb-1">
                              {t("auth.onboarding.organization.magicFillIntroTitle")}
                            </span>
                            {t("auth.onboarding.organization.magicFillIntroBody")}
                          </>
                        }
                        onHintAutoClose={dismissMagicFillIntro}
                      />
                    </div>
                    {/* Nome da Organização */}
                    <FormField
                      control={organizationForm.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium flex items-center gap-1">
                            {t("auth.onboarding.organization.nameLabel")}
                            <span className="text-destructive">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t("auth.onboarding.organization.namePlaceholder")}
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                // Resetar flag para permitir novo auto-trigger
                                setHasTriggeredAutoFill(false);
                              }}
                              onBlur={() => {
                                field.onBlur();
                                handleAutoFillTrigger();
                              }}
                              className="h-11"
                              disabled={isSubmitting}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Website */}
                    <FormField
                      control={organizationForm.control}
                      name="website"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium flex items-center gap-1">
                            {t("auth.onboarding.organization.websiteLabel")}
                            <span className="text-destructive">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="exemplo.com"
                              {...field}
                              className="h-11"
                              type="text"
                              disabled={isSubmitting}
                              onChange={(e) => {
                                field.onChange(e);
                                // Resetar flag para permitir novo auto-trigger
                                setHasTriggeredAutoFill(false);
                              }}
                              onBlur={(e) => {
                                const normalized = normalizeUrl(e.target.value);
                                field.onChange(normalized);
                                field.onBlur();
                                handleAutoFillTrigger();
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Descrição */}
                    <FormField
                      control={organizationForm.control}
                      name="info"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium flex items-center gap-1">
                            {t("auth.onboarding.organization.infoLabel")}
                            <span className="text-destructive">*</span>
                          </FormLabel>
                          <FormControl>
                            <TextareaWithMagicFill
                              placeholder={t("auth.onboarding.organization.infoPlaceholder")}
                              {...field}
                              className="min-h-[80px]"
                              disabled={isSubmitting}
                              rows={3}
                              formType="organization_onboarding"
                              fieldKey="info"
                              getCurrentValues={() => organizationForm.getValues()}
                              metadata={{ onboarding: true }}
                              onMagicFillApply={(value) => {
                                organizationForm.setValue("info", value, { shouldValidate: true, shouldDirty: true });
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Setor/Indústria */}
                    <FormField
                      control={organizationForm.control}
                      name="industry"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium flex items-center gap-1">
                            {t("auth.onboarding.organization.industryLabel")}
                            <span className="text-destructive">*</span>
                          </FormLabel>
                          <FormControl>
                            <InputWithMagicFill
                              placeholder={t("auth.onboarding.organization.industryPlaceholder")}
                              {...field}
                              className="h-11"
                              disabled={isSubmitting}
                              formType="organization_onboarding"
                              fieldKey="industry"
                              getCurrentValues={() => organizationForm.getValues()}
                              metadata={{ onboarding: true }}
                              onMagicFillApply={(value) => {
                                organizationForm.setValue("industry", value, { shouldValidate: true, shouldDirty: true });
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Tamanho da Empresa */}
                    <FormField
                      control={organizationForm.control}
                      name="size"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium flex items-center gap-1">
                            {t("auth.onboarding.organization.sizeLabel")}
                            <span className="text-destructive">*</span>
                          </FormLabel>
                          <Select onValueChange={field.onChange} value={field.value} disabled={isSubmitting || isMagicFillLoading}>
                            <FormControl>
                              <SelectTrigger className="h-11">
                                <SelectValue placeholder={t("auth.onboarding.organization.sizePlaceholder")} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {COMPANY_SIZES.map((size) => (
                                <SelectItem key={size.value} value={size.value}>
                                  {t(size.labelKey)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Estágio da Empresa */}
                    <FormField
                      control={organizationForm.control}
                      name="stage"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium flex items-center gap-1">
                            {t("auth.onboarding.organization.stageLabel")}
                            <span className="text-destructive">*</span>
                          </FormLabel>
                          <Select onValueChange={field.onChange} value={field.value} disabled={isSubmitting || isMagicFillLoading}>
                            <FormControl>
                              <SelectTrigger className="h-11">
                                <SelectValue placeholder={t("auth.onboarding.organization.stagePlaceholder")} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {COMPANY_STAGES.map((stage) => (
                                <SelectItem key={stage.value} value={stage.value}>
                                  {t(stage.labelKey)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Coluna Direita - Logo e Planos */}
                  <div className="flex flex-col gap-6 bg-muted/50 p-8">
                    {/* Upload da Logo */}
                    <div className="flex flex-col items-center space-y-3 py-6">
                      <input
                        ref={logo.inputRef}
                        type="file"
                        accept="image/*"
                        onChange={logo.handleChange}
                        className="hidden"
                        disabled={isSubmitting}
                      />
                      
                      <div className="relative">
                        {logo.preview ? (
                          <div className="relative group">
                            {/* Logo com overlay hover */}
                            <div className="w-32 h-32 rounded-2xl overflow-hidden border-4 border-primary/20 shadow-2xl bg-gradient-to-br from-primary/10 to-primary/5 transition-all group-hover:border-primary/30 group-hover:shadow-primary/20 flex items-center justify-center">
                              <img
                                src={logo.preview}
                                alt="Logo preview"
                                className="max-w-full max-h-full object-contain"
                              />
                            </div>
                            
                            {/* Action buttons overlay */}
                            <div className="absolute inset-0 rounded-2xl bg-black/0 group-hover:bg-black/40 transition-all duration-200 flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="icon"
                                  className="h-10 w-10 rounded-full shadow-lg bg-white hover:bg-white/90"
                                  onClick={() => logo.inputRef.current?.click()}
                                  disabled={isSubmitting}
                                >
                                  <Upload className="h-4 w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="icon"
                                  className="h-10 w-10 rounded-full shadow-lg"
                                  onClick={logo.handleRemove}
                                  disabled={isSubmitting}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => logo.inputRef.current?.click()}
                            disabled={isSubmitting}
                            className="relative w-32 h-32 rounded-2xl border-3 border-dashed border-primary/30 bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 hover:from-primary/10 hover:via-primary/15 hover:to-primary/10 hover:border-primary/50 transition-all duration-300 flex flex-col items-center justify-center group shadow-lg hover:shadow-xl hover:shadow-primary/10"
                          >
                            {/* Icon container */}
                            <div className="w-14 h-14 rounded-xl bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center transition-all duration-300 group-hover:scale-110">
                              <Upload className="h-7 w-7 text-primary/60 group-hover:text-primary transition-colors" />
                            </div>
                            
                            {/* Animated ring */}
                            <div className="absolute inset-0 rounded-2xl border-2 border-primary/0 group-hover:border-primary/20 transition-all duration-300" />
                          </button>
                        )}
                      </div>
                      
                      {/* Helper text */}
                      <p className="text-xs text-center text-muted-foreground">
                        Logo
                      </p>
                    </div>
                    
                    {/* Seção de Planos */}
                    <div className={`rounded-lg border ${planError ? 'border-destructive' : 'border-border/50'} bg-background/80 p-4 h-fit sticky top-24`}>
                      {selectedPlan ? (
                        <div className="flex flex-col gap-3">
                          <div className="relative text-center pb-3 border-b border-border/50">
                            <button
                              type="button"
                              onClick={() => setSelectedPlan(null)}
                              className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-foreground hover:bg-foreground/80 flex items-center justify-center transition-colors cursor-pointer"
                              title={i18n.language.startsWith('pt') ? 'Remover plano' : 'Remove plan'}
                            >
                              <svg className="w-4 h-4 text-background" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                            <p className="text-xs text-muted-foreground mb-1">
                              {i18n.language.startsWith('pt') ? 'Plano selecionado' : 'Selected plan'}
                            </p>
                            <button
                              type="button"
                              onClick={() => setIsPlanSelectorOpen(true)}
                              className="font-semibold text-lg text-blue-600 hover:text-blue-700 hover:underline cursor-pointer"
                            >
                              {selectedPlan.name}
                            </button>
                          </div>
                          
                          {/* Recursos inclusos */}
                          {selectedPlan.content.features && selectedPlan.content.features.length > 0 && (
                            <div className="text-left">
                              <h4 className="text-xs font-medium mb-2 text-muted-foreground">
                                {i18n.language.startsWith('pt') ? 'Recursos inclusos:' : 'Included features:'}
                              </h4>
                              <ul className="space-y-1">
                                {selectedPlan.content.features.slice(0, 4).map((feature, index) => (
                                  <li key={index} className="flex items-start gap-1.5 text-xs">
                                    <span className="text-primary mt-0.5">✓</span>
                                    <span className="text-muted-foreground">{getLocalizedText(feature, i18n.language.split('-')[0])}</span>
                                  </li>
                                ))}
                                {selectedPlan.content.features.length > 4 && (
                                  <li className="text-xs text-muted-foreground pl-4 pt-1">
                                    +{selectedPlan.content.features.length - 4} {i18n.language.startsWith('pt') ? 'recursos' : 'features'}
                                  </li>
                                )}
                              </ul>
                            </div>
                          )}
                          
                          <button
                            type="button"
                            onClick={() => setIsPlanSelectorOpen(true)}
                            className="text-xs text-primary hover:underline"
                          >
                            {i18n.language.startsWith('pt') ? 'Alterar plano' : 'Change plan'}
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setIsPlanSelectorOpen(true)}
                          className="w-full py-3 text-center hover:bg-muted/50 rounded-md transition-colors"
                        >
                          <p className="text-xs text-muted-foreground mb-1">
                            {i18n.language.startsWith('pt') ? 'Assinatura' : 'Subscription'}
                          </p>
                          <p className="text-sm font-medium text-primary">
                            {i18n.language.startsWith('pt') ? 'Escolher plano →' : 'Choose plan →'}
                          </p>
                        </button>
                      )}
                    </div>

                    {/* Espaçador para empurrar o botão para baixo */}
                    <div className="flex-1" />
                  </div>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
        </div>
        
        {/* Mensagem de erro geral */}
        <div className={`text-xs text-center -mt-4 h-4 transition-opacity ${
          Object.keys(organizationForm.formState.errors).length > 0 || planError
            ? 'text-destructive opacity-100' 
            : 'opacity-0'
        }`}>
          {planError || t('auth.onboarding.validation.requiredFieldsError')}
        </div>
        <div data-form-complete={isOrganizationFormComplete} className="hidden" />
      </div>

      {/* Modal de Pagamento Pendente */}
      {pendingInvoice && (
        <PendingPaymentModal
          isOpen={isPaymentModalOpen}
          invoice={pendingInvoice}
          onPay={() => {
            if (pendingInvoice) {
              // Track payment initiation
              analyticsService.trackEvent('payment_initiated', {
                invoice_id: pendingInvoice.id,
                amount: pendingInvoice.amount,
                currency: pendingInvoice.currency
              });
              
              window.location.href = `/invoices/${pendingInvoice.id}/payment`;
            }
          }}
          onContinueLimited={() => {
            if (pendingInvoice) {
              // Track payment skip
              analyticsService.trackEvent('payment_skipped', {
                invoice_id: pendingInvoice.id,
                reason: 'user_choice'
              });
            }
            
            window.location.href = '/';
          }}
        />
      )}

      {/* Modal de Waitlist/Validator */}
      <ValidatorModal
        isOpen={isWaitlistModalOpen}
        onRequestAccess={handleRequestAccess}
      />

      {/* Modal de Sucesso do Trial */}
      <TrialSuccessModal
        isOpen={isTrialSuccessModalOpen}
        trialEndDate={trialEndDate}
        credits={trialCredits}
        onContinue={() => {
          window.location.href = '/';
        }}
      />
    </MagicFillProvider>
  );
};
